import { Container, Row } from "react-bootstrap";

function LegalPage() {
  return (
    <Container fluid className="legal px-3 px-sm-5 mb-5">
      <Row>
        <h1>Script Hive: Terms of Service, DMCA, &amp; Privacy Policy</h1>
        <p className="text-muted">Last updated: July 19, 2026</p>

        <p className="text-muted">
          Script Hive is a free educational screenplay library created to
          support the study of screenwriting. We respect the rights of
          copyright holders, honor valid takedown requests, remove personal
          information from screenplays whenever identified, and use reasonable
          technical measures to protect the platform from automated abuse and
          unauthorized scraping.
        </p>

        <p className="text-muted">
          For takedown requests or questions, please contact us.
        </p>

        <p>
          <a href="mailto:support@scripthive.com">
            <button className="btn btn-primary">Contact Us</button>
          </a>
        </p>
      </Row>

      <Row className="mt-4">
        <h2>Privacy Policy</h2>
        <p className="text-muted">
          Your privacy is important to us. It is Script Hive's policy to
          respect your privacy regarding any information we may collect through
          our website and related services.
        </p>

        <h3 className="fs-5 mt-3">1. Information We Collect</h3>
        <ul className="text-muted">
          <li>
            <strong>Log Data:</strong> When you visit our website, our servers
            may automatically log standard information provided by your web
            browser, including your IP address, browser type, pages visited,
            and time spent on the site.
          </li>
          <li>
            <strong>Device Data:</strong> We may collect information about the
            device you use to access Script Hive, including device type,
            operating system, and unique device identifiers.
          </li>
          <li>
            <strong>Account Data:</strong> To access the screenplay database,
            users voluntarily provide an email address to create an account.
          </li>
        </ul>

        <h3 className="fs-5 mt-3">2. Legal Bases for Processing (GDPR)</h3>
        <p className="text-muted">
          We process personal information lawfully, fairly, and transparently.
          We collect and use your information only when it is necessary to:
        </p>
        <ul className="text-muted">
          <li>provide access to your account and services;</li>
          <li>
            fulfill our legitimate interests, including protecting Script Hive
            from malicious scraping and abuse;
          </li>
          <li>comply with legal obligations; or</li>
          <li>obtain your consent where required by law.</li>
        </ul>
        <p className="text-muted">
          We retain personal information only for as long as reasonably
          necessary and implement reasonable administrative and technical
          safeguards designed to protect it from unauthorized access,
          disclosure, alteration, or misuse.
        </p>

        <h3 className="fs-5 mt-3">3. Collection and Use of Information</h3>
        <p className="text-muted">
          We may collect, hold, and use personal information to:
        </p>
        <ul className="text-muted">
          <li>provide and maintain your account;</li>
          <li>personalize your experience;</li>
          <li>
            communicate with you regarding your account or our services; and
          </li>
          <li>comply with applicable legal obligations.</li>
        </ul>

        <h3 className="fs-5 mt-3">4. Disclosure of Personal Information</h3>
        <p className="text-muted">
          We may disclose personal information only to trusted service
          providers who help operate Script Hive, such as website hosting,
          cloud storage, database infrastructure, and other essential technical
          providers.
        </p>
        <p className="text-muted">
          <strong>
            We do not sell, rent, or share your personal information for
            advertising or marketing purposes.
          </strong>
        </p>

        <h3 className="fs-5 mt-3">5. Cookies and Local Storage</h3>
        <p className="text-muted">
          Script Hive uses browser local storage to remember preferences such
          as theme selection, custom lists, and login consent choices. This
          information remains on your device and is not transmitted to our
          servers or shared with advertising or tracking networks.
        </p>

        <h3 className="fs-5 mt-3">6. Cloudflare Turnstile</h3>
        <p className="text-muted">
          Script Hive uses Cloudflare Turnstile to help protect the website
          from spam, automated abuse, malicious bots, and unauthorized
          scraping.
        </p>
        <p className="text-muted">
          When interacting with protected forms or services, Turnstile may
          collect technical information such as your IP address, browser
          characteristics, and other signals necessary to determine whether a
          visitor is human. This information is processed by Cloudflare to help
          protect the security and integrity of Script Hive.
        </p>

        <h3 className="fs-5 mt-3">7. Artificial Intelligence &amp; Automated Access</h3>
        <p className="text-muted">
          <strong>AI Training Restrictions</strong>
        </p>
        <p className="text-muted">
          Script Hive prohibits the use of automated systems&mdash;including
          artificial intelligence models, machine learning systems, large
          language models, bots, crawlers, robots, or similar
          technologies&mdash;to access, scrape, download, copy, or use
          screenplay content for the purpose of creating datasets or training
          AI models without prior written permission.
        </p>
        <p className="text-muted">
          <strong>Our Use of AI</strong>
        </p>
        <p className="text-muted">
          Script Hive does not use screenplay content contained in its library
          or user personal information to train artificial intelligence,
          machine learning, or large language models.
        </p>

        <h3 className="fs-5 mt-3">8. Children's Privacy</h3>
        <p className="text-muted">
          Script Hive is not directed to children under the age of 13 and does
          not knowingly collect personal information from children under 13.
          If we become aware that we have collected personal information from a
          child without appropriate consent, we will take reasonable steps to
          delete that information promptly.
        </p>

        <h3 className="fs-5 mt-3">9. California Privacy Rights (CCPA)</h3>
        <p className="text-muted">
          If you are a California resident, you have the right to know what
          personal information we collect, request deletion of your personal
          information, and opt out of the sale of your personal information.
          Script Hive does not sell your personal information. To exercise
          these rights, please contact us at:{" "}
          <a href="mailto:support@scripthive.com">support@scripthive.com</a>
        </p>
      </Row>

      <Row className="mt-4">
        <h2>Terms of Service</h2>

        <h3 className="fs-5 mt-3">1. Access &amp; Acceptable Use</h3>
        <p className="text-muted">
          Script Hive grants users a limited, revocable, non-exclusive license
          to access and view materials on the website for personal,
          non-commercial educational use only.
        </p>
        <p className="text-muted">By using Script Hive, you agree not to:</p>
        <ul className="text-muted">
          <li>
            use bots, crawlers, robots, web scrapers, artificial intelligence
            systems, large language models, or any other automated means to
            access, query, scrape, copy, download, or harvest content from the
            website except as expressly authorized in writing;
          </li>
          <li>
            circumvent or attempt to bypass download limits, access
            restrictions, bot detection systems (including Cloudflare
            Turnstile), or other security measures;
          </li>
          <li>
            interfere with the security, stability, or operation of Script
            Hive;
          </li>
          <li>redistribute screenplay files in bulk;</li>
          <li>upload malware, malicious code, or other harmful software;</li>
          <li>
            attempt unauthorized access to user accounts, servers, or
            databases; or
          </li>
          <li>
            use Script Hive content to create datasets or train artificial
            intelligence, machine learning, or large language models without
            prior written permission.
          </li>
        </ul>

        <h3 className="fs-5 mt-3">2. User Accounts</h3>
        <p className="text-muted">
          Users are responsible for maintaining the confidentiality of their
          account credentials and for all activity occurring under their
          account.
        </p>
        <p className="text-muted">
          You agree to notify Script Hive promptly of any unauthorized use of
          your account or any other security breach.
        </p>

        <h3 className="fs-5 mt-3">3. Patreon &amp; Community Donations</h3>
        <p className="text-muted">Script Hive is entirely free to use.</p>
        <p className="text-muted">
          To help offset the costs of hosting, bandwidth, domains, and ongoing
          platform development, we accept voluntary community support through
          Patreon.
        </p>
        <p className="text-muted">
          Any digital perks provided to Patrons&mdash;including dark mode,
          script favoriting, Discord VIP roles, or similar features&mdash;are
          offered solely as community appreciation benefits.
        </p>
        <p className="text-muted">
          These perks do not constitute the purchase of content, confer any
          ownership rights, or grant access to proprietary materials. These
          benefits may be modified or discontinued at any time.
        </p>

        <h3 className="fs-5 mt-3">4. Accuracy of Materials</h3>
        <p className="text-muted">
          Script Hive makes reasonable efforts to provide accurate and complete
          screenplay files for educational purposes.
        </p>
        <p className="text-muted">
          However, we do not guarantee that any screenplay is complete,
          current, final, error-free, or identical to the version originally
          produced, published, or filmed.
        </p>
        <p className="text-muted">
          Screenplays may contain transcription errors, formatting differences,
          omitted pages, revisions, or other inaccuracies. Users should
          independently verify information before relying upon it.
        </p>

        <h3 className="fs-5 mt-3">5. Disclaimer &amp; Limitation of Liability</h3>
        <p className="text-muted">
          The materials and services provided by Script Hive are offered on an
          "as is" and "as available" basis.
        </p>
        <p className="text-muted">
          Script Hive disclaims all warranties, express or implied, including
          warranties of merchantability, fitness for a particular purpose,
          accuracy, availability, or reliability.
        </p>
        <p className="text-muted">
          To the fullest extent permitted by law, Script Hive and its
          administrators shall not be liable for any damages, including loss of
          data, interruption of service, or loss of profits, arising from the
          use of, or inability to use, the website.
        </p>

        <h3 className="fs-5 mt-3">6. Governing Law</h3>
        <p className="text-muted">
          These Terms shall be governed by and construed in accordance with the
          laws of the State of New York, without regard to its conflict of law
          principles.
        </p>
        <p className="text-muted">
          Any legal action arising out of or relating to these Terms shall be
          brought in the state or federal courts located in the State of New
          York, and you consent to the jurisdiction of those courts.
        </p>

        <h3 className="fs-5 mt-3">7. Copyright Ownership</h3>
        <p className="text-muted">
          All copyrights and other intellectual property rights in the
          screenplays and related materials remain the property of their
          respective copyright owners.
        </p>
        <p className="text-muted">
          Nothing in these Terms transfers ownership of any screenplay or other
          copyrighted material to users or grants any right to reproduce,
          redistribute, publicly display, or commercially exploit such
          materials.
        </p>

        <h3 className="fs-5 mt-3">8. Account Suspension &amp; Termination</h3>
        <p className="text-muted">
          Script Hive reserves the right to suspend or terminate user accounts
          that violate these Terms, including:
        </p>
        <ul className="text-muted">
          <li>excessive automated access;</li>
          <li>scraping;</li>
          <li>attempts to circumvent download limits;</li>
          <li>abuse of the service;</li>
          <li>attempts to bypass security measures; or</li>
          <li>
            any activity that threatens the security, stability, or operation
            of the platform.
          </li>
        </ul>
      </Row>

      <Row className="mt-4">
        <h2>Fair Use &amp; DMCA Copyright Claims</h2>

        <h3 className="fs-5 mt-3">Fair Use Statement</h3>
        <p className="text-muted">
          Script Hive is an educational resource that provides screenplays for
          research, scholarship, criticism, and the study of screenwriting.
        </p>
        <p className="text-muted">
          We respect the rights of copyright holders and promptly respond to
          valid copyright concerns and takedown requests in accordance with the
          Digital Millennium Copyright Act (DMCA).
        </p>

        <h3 className="fs-5 mt-3">DMCA Copyright Takedown Policy</h3>
        <p className="text-muted">
          In accordance with the Digital Millennium Copyright Act (17 U.S.C.
          &sect; 512), Script Hive will promptly review and respond to valid
          DMCA notices and, where appropriate, remove or disable access to
          allegedly infringing material in accordance with applicable law.
        </p>
        <p className="text-muted">
          If you believe that copyrighted material available on Script Hive
          infringes your rights, please send a notice to our Designated
          Copyright Agent containing:
        </p>
        <ul className="text-muted">
          <li>The URL(s) of the allegedly infringing material.</li>
          <li>Your name and contact information.</li>
          <li>
            A statement that you have a good-faith belief that the use of the
            material is not authorized by the copyright owner, its agent, or
            the law.
          </li>
          <li>
            A statement made under penalty of perjury that the information
            contained in the notice is accurate and that you are authorized to
            act on behalf of the copyright owner.
          </li>
          <li>Your physical or electronic signature.</li>
        </ul>
        <p className="text-muted">
          <strong>Designated Copyright Agent:</strong>{" "}
          <a href="mailto:support@scripthive.com">support@scripthive.com</a>
        </p>
        <p className="text-muted">
          <strong>Mailing Address:</strong> PO BOX 14812, 30 Karner Road,
          Albany, NY 12212
        </p>
      </Row>
    </Container>
  );
}

export default LegalPage;
